import React, { useMemo } from "react";
import { sanitizeHtml } from "../../../utils/sanitizeHtml";
import { doPrzenosnego } from "./htmlPrzenosny";

/**
 * JEDNO WYŚWIETLANIE dokumentu pisanego w edytorze treści — regulaminu i komunikatu.
 *
 * Właściciel (2026-09-08): „ten regulamin w takiej formie ma być widoczny w centrum powiadomień
 * w zakładce regulaminy, oraz to samo dotyczy wszystkich innych komunikatów, które też tutaj są
 * widoczne, a które programowane w treści są w /admin/announcements".
 *
 * DWA POWODY, DLA KTÓRYCH TO JEST FUNKCJA, A NIE `DOMPurify.sanitize(x)` W KAŻDYM MIEJSCU:
 *
 *  1. FORMATOWANIE MA PRZEŻYĆ. Treści zapisane starszym edytorem trzymają wyrównanie i wcięcie
 *     w KLASACH Quilla (`ql-align-center`, `ql-indent-2`), a rodzaj listy w `data-list` — czyli
 *     w rzeczach, których żaden z tutejszych ekranów nie ma w CSS i które twarda sanityzacja
 *     wycina. `doPrzenosnego` zamienia je na style inline ZANIM cokolwiek zostanie wycięte,
 *     więc stary komunikat wyświetla się tak samo dobrze jak nowy.
 *
 *  2. TA SAMA OCHRONA WSZĘDZIE. Trzy miejsca renderujące komunikaty wołały gołe
 *     `DOMPurify.sanitize(content)` — konfiguracja domyślna, która (zweryfikowane w [A16-03],
 *     `utils/sanitizeHtml.js`) przepuszcza `<form>`, `<input type=password>`, `<button>`, `<svg>`
 *     i dowolny `style`, łącznie z `position: fixed`. Treść komunikatu widzi KAŻDY pracownik,
 *     więc idzie tą samą twardą allowlistą co poczta w zgłoszeniach.
 *
 * Kolejność jest istotna: NAJPIERW normalizacja (potrzebuje klas), POTEM sanityzacja (klasy usuwa).
 * Normalizacja pracuje na osobnym dokumencie bez kontekstu przeglądania — nic się nie wykonuje
 * i nic nie jest pobierane.
 */
/**
 * [polecenie właściciela 2026-09-14] „tekst ucieka poza okno i trzeba przesuwać w bok".
 * PRZYCZYNA (zmierzona na komunikacie z produkcji): treść wklejona z Worda/Outlooka ma TWARDĄ SPACJĘ
 * (`&nbsp;`) między KAŻDYM słowem — przeglądarka nie ma gdzie złamać linii, więc akapit jedzie w bok.
 * Na ekranie zamieniamy twarde spacje na zwykłe; zapis w bazie zostaje nietknięty.
 */
export const zwykleSpacje = (html) => String(html || "").replace(/&nbsp;|&#160;|\u00A0/g, " ");

export const htmlDokumentuNaEkran = (surowyHtml) => {
    if (!surowyHtml) return "";
    // `allowRemoteImages` — treść pisze administrator w panelu, nie nadawca z zewnątrz, więc obrazek
    // wstawiony świadomie w komunikacie ma się pokazać bez paska „Pokaż obrazy" (patrz A16-07).
    return sanitizeHtml(doPrzenosnego(zwykleSpacje(surowyHtml)), { allowRemoteImages: true });
};

/**
 * Gotowy blok treści. `className` podaje strona wywołująca — każdy ekran ma własną typografię
 * (okno bramki, centrum powiadomień, podgląd w panelu), wspólne jest CZYSZCZENIE I NORMALIZACJA.
 */
export const TrescDokumentu = ({ html, className }) => {
    /* [EA-2 poprawki 2026-10-03, znalezisko własne] React 19 porównuje `dangerouslySetInnerHTML` po TOŻSAMOŚCI obiektu: nowy
       `{ __html }` przy każdym renderze rodzica nadpisywał innerHTML tym samym napisem. Zrzuty z materiałów procedury, wczytane z tokenem
       PO wyrenderowaniu, wracały wtedy do pustych zastępników (np. przy przewijaniu potwierdzenia zapoznania albo po kliknięciu obrazu).
       Ten sam HTML = ten sam obiekt = DOM treści nietknięty; zmiana HTML — nowa treść jak dotąd. */
    const tresc = useMemo(() => ({ __html: htmlDokumentuNaEkran(html) }), [html]);
    return <div className={className} data-tresc-dokumentu dangerouslySetInnerHTML={tresc} />;
};

export default TrescDokumentu;
